import { useNamespaceSelector } from '@omaze/redux';
import classNames from 'classnames';
import React, {
    ReactElement,
    useEffect,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { Dispatch } from 'redux';

import { IDestructor } from '../../shared/types/IDestructor';
import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';

import { createRemoveAction } from './snackbar-module/actions/RemoveSnackbarMessage/RemoveSnackbarMessage';
import { selectSnackbarMessages } from './snackbar-module/selectors/selectSnackbarMessages';
import { ISnackbarMessage } from './snackbar-module/Snackbar.state';
import styles from './Snackbar.module.css';
import { SNACKBAR_TYPE } from './SNACKBAR_TYPE';

export type ISnackbarProps = IQATestableProps & IStyleableProps;

type ISnackMessageProps = IQATestableProps & IStyleableProps & {
    message: ISnackbarMessage;
    forceClose: boolean;
};

const SNACKBAR_TRANSITION_DURATION: number = 600;
const SNACKBAR_DURATION: number = 2000;
const SNACKBAR_MINIMUM_DURATION: number = 500;

function OzSnackMessage (props: ISnackMessageProps): ReactElement | null {
    const {
        message: {
            id,
            message,
            type,
        },
        className,
        'data-testid': dataTestId,
        forceClose,
    }: ISnackMessageProps = props;

    const [show, setShow]: [boolean, ISetState<boolean>] = useState<boolean>(true);
    const [timeout, setTimeout]: [number, ISetState<number>] = useState<number>(-1);
    const [minimumDelayElapsed, setMinimumDelayElapsed]: [boolean, ISetState<boolean>] = useState<boolean>(false);
    const dispatch: Dispatch = useDispatch();

    useEffect((): IDestructor => {
        const timeout: number = window.setTimeout((): void => {
            setMinimumDelayElapsed(true);
        }, SNACKBAR_TRANSITION_DURATION + SNACKBAR_MINIMUM_DURATION);

        return (): void => {
            clearTimeout(timeout);
        };
    }, []);

    useEffect((): IDestructor => {
        setTimeout(window.setTimeout((): void => {
            setShow(false);
        }, SNACKBAR_TRANSITION_DURATION + SNACKBAR_DURATION));

        return (): void => {
            if (!timeout) {
                return;
            }

            clearTimeout(timeout);
        };
    }, []);

    useEffect((): void => {
        if (!forceClose || !minimumDelayElapsed) {
            return;
        }

        if (timeout) {
            clearTimeout(timeout);
        }

        setShow(false);
    }, [forceClose, minimumDelayElapsed]);

    const snackbarClassNames: string = classNames({
        'bg-green-500': type === SNACKBAR_TYPE.SUCCESS,
        'bg-red-500': type === SNACKBAR_TYPE.ERROR,
        [className + '']: !!className,
    });

    return (
        <CSSTransition
            in={show}
            timeout={SNACKBAR_TRANSITION_DURATION}
            appear
            classNames={{
                appear: styles.default,
                appearActive: styles.visible,
                appearDone: styles.visible,
                exitActive: styles.default,
            }}
            onExited={(): void => {
                dispatch(createRemoveAction(id));
            }}
        >
            <div
                className={`
                transition duration-600 text-white-500 text-base px-3 py-4 text-center w-full fixed z-10 left-0 bottom-0 lg:px-9
                ${snackbarClassNames}
            `}
                data-testid={dataTestId || ''}
            >
                {message}
            </div>
        </CSSTransition>
    );
}

export function OzSnackbar (props: ISnackbarProps): ReactElement | null {
    const {
        className,
        'data-testid': dataTestId,
    }: ISnackbarProps = props;

    const messages: ISnackbarMessage[] = useNamespaceSelector(selectSnackbarMessages);

    if (!messages.length) {
        return null;
    }

    return (
        <OzSnackMessage
            key={messages[0].id}
            message={messages[0]}
            className={className}
            data-testid={dataTestId}
            forceClose={messages.length > 1}
        />
    );
}
